<template>
  <iColumn
    ref="stage"
    width="fill"
    height="fill"
    position="relative"
    overflow="hidden"
    data-test="call-stage"
    :data-layout="layoutMode"
  >
    <iColumn
      v-for="placed in placedTiles"
      :key="placed.tile.id"
      position="absolute"
      :position-placements="placed.placement"
      :width="placed.width"
      :height="placed.height"
      :transition="tileTransition"
      :opacity="placed.isEntering ? 0 : 1"
      :z-index="placed.role === 'thumb' ? 2 : 1"
      :data-entering="placed.isEntering ? 'true' : 'false'"
    >
      <StageTile
        :tile="placed.tile"
        :role="placed.role"
        :is-speaking="isParticipantSpeaking(placed.tile.id)"
        :is-pinned="placed.tile.id === focusedPeerId"
        :is-local-video-mirrored="isLocalVideoMirrored"
        :is-camera-enabled="isCameraEnabled"
        :is-microphone-enabled="isMicrophoneEnabled"
        :sink-id="selectedOutputDeviceId"
        @click="handleTileClick(placed)"
        @stop-presenting="$emit('stop-presenting')"
      />
    </iColumn>

    <iColumn
      v-if="overflowPlacement"
      position="absolute"
      :position-placements="overflowPlacement.placement"
      :width="overflowPlacement.width"
      :height="overflowPlacement.height"
      :transition="tileTransition"
      data-test="stage-overflow-tile"
      :data-hidden-count="arrangement.overflow.length"
    >
      <MoreParticipantsTile
        :preview-items="overflowPreview"
        :hidden-count="arrangement.overflow.length"
        @click="$emit('show-participants')"
      />
    </iColumn>
  </iColumn>
</template>

<script>
import StageTile from "%/apps/video-engine/components/video-call/stage/StageTile.vue";
import MoreParticipantsTile from "%/apps/video-engine/components/video-call/stage/tiles/MoreParticipantsTile.vue";
import {
  capStageTiles,
  effectiveTileCap,
  enteringBox,
  filterTilesWithoutVideo,
  gridLayout,
  sidebarLayout,
  spotlightLayout,
  stageSpacing,
} from "%/apps/video-engine/stage-layout";
import { tileVideoSource } from "%/apps/video-engine/video-subscriptions";

const STAGE_LAYOUT_MODES = ["tiled", "spotlight", "sidebar"];
const EMPTY_ARRANGEMENT = Object.freeze({ placed: [], overflow: [], overflowBox: null });
const HIGH_QUALITY_MIN_WIDTH = 640;
const MEDIUM_QUALITY_MIN_WIDTH = 320;
const TILE_EASING = "cubic-bezier(0.2, 0, 0, 1)";
const TILE_TRANSITION = ["left", "top", "width", "height"].map(property => `${property} 0.32s ${TILE_EASING}`)
    .concat(`opacity 0.24s ${TILE_EASING}`)
    .join(", ");
const PANEL_OPEN_SECONDS = 0.23;
const PANEL_CLOSE_SECONDS = 0.27;
const settleTransition = seconds => ["left", "top", "width", "height"].map(property => `${property} ${seconds}s ${TILE_EASING}`)
    .concat(`opacity ${seconds}s ${TILE_EASING}`)
    .join(", ");
const ENTERING_SCALE = 0.86;
const RESIZE_SETTLE_MS = 120;

function prefersReducedMotion() {
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

const px = value => `${Math.round(value)}px`;

export default {
  name: "CallStage",
  components: { StageTile, MoreParticipantsTile },
  props: {
    tiles: {
      type: Array,
      default: () => [],
    },
    layoutMode: {
      type: String,
      default: "tiled",
      validator: mode => STAGE_LAYOUT_MODES.includes(mode),
    },
    primaryPeerId: {
      type: String,
      default: null,
    },
    focusedPeerId: {
      type: String,
      default: null,
    },
    maxTiles: {
      type: Number,
      default: 16,
    },
    hideTilesWithoutVideo: {
      type: Boolean,
      default: false,
    },
    settleDelta: {
      type: Number,
      default: null,
    },
    isParticipantSpeaking: {
      type: Function,
      default: () => false,
    },
    isLocalVideoMirrored: {
      type: Boolean,
      default: true,
    },
    isCameraEnabled: {
      type: Boolean,
      default: true,
    },
    isMicrophoneEnabled: {
      type: Boolean,
      default: true,
    },
    selectedOutputDeviceId: {
      type: String,
      default: null,
    },
  },
  emits: ["focus-tile", "restore-speaker-view", "subscription-plan", "stop-presenting", "show-participants"],
  data() {
    return {
      stageWidth: 0,
      stageHeight: 0,
      resizeObserver: null,
      revealedTileIds: {},
      isStageResizing: false,
      heldColumns: null,
      panelColumns: null,
      settleBaseWidth: null,
    };
  },
  computed: {
    tileTransition() {
      if (prefersReducedMotion() || this.isStageResizing) {
        return "none";
      }

      if (!this.isSettling) {
        return TILE_TRANSITION;
      }

      return settleTransition(this.settleDelta < 0 ? PANEL_OPEN_SECONDS : PANEL_CLOSE_SECONDS);
    },
    isSettling() {
      return this.settleDelta !== null && this.settleBaseWidth !== null;
    },
    layoutWidth() {
      return this.isSettling ? this.settleBaseWidth + this.settleDelta : this.stageWidth;
    },
    spacing() {
      return stageSpacing(this.layoutWidth);
    },
    stageBox() {
      return { width: this.layoutWidth, height: this.stageHeight, ...this.spacing };
    },
    primaryTile() {
      const wanted = this.focusedPeerId || this.primaryPeerId;

      return this.tiles.find(tile => tile.id === wanted) || this.tiles[0] || null;
    },
    tilesByVideo() {
      if (!this.hideTilesWithoutVideo) {
        return { shown: this.tiles, hidden: [] };
      }
      const keepIds = [this.focusedPeerId, this.layoutMode === "tiled" ? null : this.primaryTile?.id].filter(Boolean);

      return filterTilesWithoutVideo(this.tiles, { isCameraEnabled: this.isCameraEnabled, keepIds });
    },
    tiledArrangement() {
      const { shown, hidden } = this.tilesByVideo;
      const cap = effectiveTileCap({ maxTiles: this.maxTiles, ...this.stageBox });
      const slotsForPeople = hidden.length ? Math.max(0, cap - 1) : cap;
      const { visible, overflow } = hidden.length
        ? { visible: shown.slice(0, slotsForPeople), overflow: [...shown.slice(slotsForPeople), ...hidden] }
        : capStageTiles(shown, cap);
      const grid = gridLayout({
        count: visible.length + (overflow.length ? 1 : 0),
        columns: this.heldColumns,
        preferredColumns: this.panelColumns,
        ...this.stageBox,
      });
      const cells = grid.boxes;

      return {
        columns: grid.columns,
        placed: visible.map((tile, index) => this.place(tile, "grid", cells[index])),
        overflow,
        overflowBox: overflow.length ? cells[visible.length] : null,
      };
    },
    sidebarArrangement() {
      const primary = this.primaryTile;
      if (!primary) {
        return EMPTY_ARRANGEMENT;
      }
      const isNotPrimary = tile => tile.id !== primary.id;
      const candidates = this.tilesByVideo.shown.filter(isNotPrimary);
      const hidden = this.tilesByVideo.hidden.filter(isNotPrimary);
      const layout = sidebarLayout({ count: candidates.length, hiddenCount: hidden.length, ...this.stageBox });
      const thumbs = candidates.slice(0, layout.thumbs.length);

      return {
        placed: [
          this.place(primary, "primary", layout.primary),
          ...thumbs.map((tile, index) => this.place(tile, "thumb", layout.thumbs[index])),
        ],
        overflow: [...candidates.slice(thumbs.length), ...hidden],
        overflowBox: layout.overflow,
      };
    },
    spotlightArrangement() {
      const primary = this.primaryTile;
      if (!primary) {
        return EMPTY_ARRANGEMENT;
      }
      const box = spotlightLayout(this.stageBox);

      return { placed: [this.place(primary, "primary", box)], overflow: [], overflowBox: null };
    },
    arrangement() {
      if (!this.stageWidth || !this.stageHeight) {
        return EMPTY_ARRANGEMENT;
      }
      if (this.layoutMode === "tiled") {
        return this.tiledArrangement;
      }
      if (this.layoutMode === "sidebar") {
        return this.sidebarArrangement;
      }
      if (this.layoutMode === "spotlight") {
        return this.spotlightArrangement;
      }
      throw new Error(`CallStage: layout ${JSON.stringify(this.layoutMode)} is not one of ${STAGE_LAYOUT_MODES.join(", ")}`);
    },
    placedTiles() {
      return this.arrangement.placed;
    },
    overflowPreview() {
      return this.arrangement.overflow.slice(0, 4).map(tile => ({ id: tile.id, name: tile.displayName || "Participant" }));
    },
    overflowPlacement() {
      const { overflowBox, overflow } = this.arrangement;

      return overflowBox && overflow.length ? this.place(null, "grid", overflowBox) : null;
    },
    subscriptionPlan() {
      const onStage = this.placedTiles
          .filter(placed => !placed.tile.isLocal)
          .map(placed => ({
            sid: placed.tile.participantSid,
            source: tileVideoSource(placed.tile),
            quality: this.qualityForWidth(placed.role, placed.width),
          }));
      const placedIds = new Set(this.placedTiles.map(placed => placed.tile.id));
      const offStage = this.tiles
          .filter(tile => !tile.isLocal && !placedIds.has(tile.id))
          .map(tile => ({ sid: tile.participantSid, source: tileVideoSource(tile), quality: null }));

      return [...onStage, ...offStage];
    },
  },
  watch: {
    settleDelta(delta) {
      if (delta !== null && this.panelColumns === null && this.layoutMode === "tiled" && this.stageWidth && this.stageHeight) {
        this.panelColumns = this.tiledArrangement.columns || null;
      }
      this.settleBaseWidth = delta === null ? null : this.stageWidth;
    },
    "tiles.length"() {
      this.panelColumns = null;
    },
    layoutMode() {
      this.panelColumns = null;
    },
    placedTiles(placed) {
      const pending = placed.filter(entry => entry.isEntering).map(entry => entry.tile.id);
      if (!pending.length) {
        return;
      }
      window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
        this.revealedTileIds = { ...this.revealedTileIds, ...Object.fromEntries(pending.map(id => [id, true])) };
      }));
    },
    subscriptionPlan: {
      handler(plan, previous) {
        if (JSON.stringify(plan) === JSON.stringify(previous)) {
          return;
        }
        this.$emit("subscription-plan", plan);
      },
      immediate: true,
    },
  },
  mounted() {
    const element = this.$refs.stage?.$el || this.$refs.stage;
    if (typeof ResizeObserver !== "undefined" && element) {
      this.resizeObserver = new ResizeObserver(entries => {
        const rect = entries[0]?.contentRect;
        if (rect) {
          this.followStageResize();
          this.stageWidth = rect.width;
          this.stageHeight = rect.height;
        }
      });
      this.resizeObserver.observe(element);
    }
    if (element) {
      this.stageWidth = element.clientWidth;
      this.stageHeight = element.clientHeight;
    }
  },
  beforeUnmount() {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    window.clearTimeout(this.resizeSettleTimer);
  },
  methods: {
    followStageResize() {
      if (this.isSettling) {
        return;
      }
      if (!this.isStageResizing && this.layoutMode === "tiled" && this.stageWidth && this.stageHeight) {
        this.heldColumns = this.tiledArrangement.columns || null;
      }
      this.isStageResizing = true;
      window.clearTimeout(this.resizeSettleTimer);
      this.resizeSettleTimer = window.setTimeout(() => {
        this.isStageResizing = false;
        this.heldColumns = null;
      }, RESIZE_SETTLE_MS);
    },
    place(tile, role, box) {
      const isEntering = Boolean(tile) && !this.revealedTileIds[tile.id] && !prefersReducedMotion();
      const shown = isEntering ? enteringBox(box, ENTERING_SCALE) : box;

      return {
        tile,
        role,
        isEntering,
        width: Math.max(0, Math.round(shown.width)),
        height: Math.max(0, Math.round(shown.height)),
        placement: { top: px(shown.top), left: px(shown.left), right: "unset", bottom: "unset" },
      };
    },
    qualityForWidth(role, width) {
      if (role === "thumb") {
        return "low";
      }
      if (role === "primary" || width >= HIGH_QUALITY_MIN_WIDTH) {
        return "high";
      }

      return width >= MEDIUM_QUALITY_MIN_WIDTH ? "medium" : "low";
    },
    handleTileClick(placed) {
      if (placed.role === "primary") {
        this.$emit("restore-speaker-view");

        return;
      }
      this.$emit("focus-tile", placed.tile);
    },
  },
};
</script>
